import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

type Score = {
  competency: string;
  avgBaseline: number;
  avgCurrent: number;
};

type ApiResponse = {
  totalStudents: number;
  scores: Score[];
};

const formatLabel = (value: string) => {
  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Inserts an invisible soft hyphen between every letter of a word. It has
// no effect unless the browser actually needs to break the word across
// lines, in which case it shows a visible "-" at whatever point it breaks
// (relies on no hyphenation dictionary, unlike CSS `hyphens: auto`, which
// Chrome frequently ignores depending on the platform's dictionary support).
const withSoftHyphens = (word: string) => word.split("").join("\u00AD");

const competencyColors: Record<string, [string, string]> = {
  SELF_AWARENESS: ["#7E57C2", "#B39DDB"],
  SELF_MANAGEMENT: ["#92C6F7", "#76B8F5"],
  SOCIAL_AWARENESS: ["#00AD5E", "#A5D6A7"],
  RELATIONSHIP_SKILLS: ["#FFCB6B", "#FFC047"],
  RESPONSIBLE_DECISION_MAKING: ["#F8A354", "#F68C2B"],
};

const BarChart = ({ data, expanded }: { data: any; expanded?: boolean }) => {
  if (!data?.scores) return null;

  const labelWidth = expanded ? 120 : 50;

  // Transform API data
  const chartData = data.scores.map((item: any) => ({
    label: formatLabel(item.competency),
    classAvg: item.avgCurrent,
    student: item.avgBaseline,
    colors: competencyColors[item.competency] || ["#888", "#CCC"],
  }));

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      height: 320,
    },

    title: { text: "" },

    subtitle: {
      useHTML: true,
      align: "right",
      floating: true, // Place subtitle in the chart area, not floating
      verticalAlign: "top",
      y: 0, // Move subtitle down a bit from the very top
      x: 0,
      text: `
        <div style="display:flex;flex-direction:column;align-items:flex-end">
          <span>Left – Baseline maturity</span>
          <span>Right – Current maturity</span>
        </div>
      `,
    },

    xAxis: {
      categories: chartData.map((d: any) => d.label),
      labels: {
        style: {
          whiteSpace: "normal",
          textOverflow: "allow",
          fontSize: "12px",
          color: "#6B6B6B",
        },
        useHTML: true,
        formatter: function() {
          const words = this.value.toString().split(' ').map(withSoftHyphens);
          const wordsPerLine = 2;
          const lines = [];
          for (let i = 0; i < words.length; i += wordsPerLine) {
            lines.push(words.slice(i, i + wordsPerLine).join(' '));
          }
          return `<div style="text-align:center; width:${labelWidth}px; white-space:normal; word-wrap:break-word; overflow-wrap:break-word; line-height:1.3em;">`
            + lines.join('<br/>')
            + '</div>';
        }
      },
      lineColor: "#666",
    },

    yAxis: {
      min: 0,
      max: 4, // because your SEL scale is 1–4
      tickInterval: 1,
      title: { text: "" },
      labels: {
        formatter: function () {
          return `L${this.value}`;
        },
        style: {
          fontSize: "12px",
          color: "#6B6B6B",
        },
      },
    },

    legend: { enabled: false },

    plotOptions: {
      column: {
        groupPadding: 0.25,
        pointPadding: 0.05,
        dataLabels: {
          enabled: false, // Disable value labels on top of bars
          // enabled: true,
          // style: {
          //   fontSize: "12px",
          //   fontWeight: "600",
          //   color: "#333",
          // },
        },
      },
    },

    series: [
      {
        type: "column",
        name: "Baseline Maturity",
        pointWidth: 13,
        data: chartData.map((d: any) => ({
          y: d.student,
          color: d.colors[0],
        })),
      },
      {
        type: "column",
        name: "Current Maturity",
        pointWidth: 13,
        data: chartData.map((d: any) => ({
          y: d.classAvg,
          color: d.colors[1],
        })),
      },
    ],

    credits: { enabled: false },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default BarChart;
